<script lang="ts">
	import type { Issue, Report } from '#lib/types';
	import { t } from '#lib/i18n/state.svelte';
	import { excerpt } from '#lib/text/normalize';
	import { designLabel } from '#lib/ui/labels';

	interface Props {
		report: Report;
		onjump: (issue: Issue) => void;
	}
	let { report, onjump }: Props = $props();

	const s = $derived(report.structure);

	const ALL_SECTIONS = [
		'abstract',
		'keywords',
		'introduction',
		'method',
		'results',
		'discussion',
		'references'
	];

	function sectionName(key: string): string {
		return t(`struct.section.${key}`);
	}

	function sectionIssue(key: string): Issue | undefined {
		return report.issues.find(
			(i) => i.code === 'struct.missing_section' && (i.meta?.sectionKey as string | undefined) === key
		);
	}

	function langChip(h: Report['language']['hits'][number]): string {
		if (h.pattern === 'causal') {
			return h.justified ? t('struct.lang.causalJustified') : t('struct.lang.causal');
		}
		if (h.pattern === 'absolutist') return t('struct.lang.absolutist');
		if (h.pattern === 'generalization') return t('struct.lang.generalization');
		if (h.pattern === 'significance') return t('struct.lang.significance');
		return h.pattern;
	}
</script>

<div class="struct-tab">
	<div class="cols">
		<section class="card panel">
			<h3>{t('struct.sectionsTitle')}</h3>
			<p class="small muted panel-sub">{t('struct.sectionsSub')}</p>
			<ul class="sections">
				{#each ALL_SECTIONS as key (key)}
					{@const found = s.sections.find((x) => x.key === key)}
					<li class:missing={!found}>
						<span class="mark" aria-hidden="true">{found ? '✓' : '—'}</span>
						<span class="sec-name">{sectionName(key)}</span>
						{#if found}
							<span class="sec-heading">{found.heading}</span>
							{#if found.line}<span class="mono sec-line">{t('common.line', { n: found.line })}</span>{/if}
						{:else}
							<span class="sec-missing">{t('struct.notFound')}</span>
							{#if sectionIssue(key)}
								<button class="btn btn-sm" onclick={() => sectionIssue(key) && onjump(sectionIssue(key)!)}>
									{t('struct.detail')}
								</button>
							{/if}
						{/if}
					</li>
				{/each}
			</ul>
			{#if s.numberingGaps.length}
				<p class="small warn-line">{t('struct.gaps')} {s.numberingGaps.join(', ')}</p>
			{/if}
		</section>

		<section class="card panel">
			<h3>{t('struct.figuresTitle')}</h3>
			<p class="small muted panel-sub">{t('struct.figuresSub')}</p>

			{#if s.tables.length === 0 && s.figures.length === 0}
				<p class="empty muted">{t('struct.noFigures')}</p>
			{:else}
				<div class="fig-block">
					<h4>{t('struct.tables')} <span class="mono count">{s.tables.length}</span></h4>
					<ul class="fig-list">
						{#each s.tables as tcap (tcap.id)}
							{@const referred = s.tableRefs.some((r) => r.number === tcap.number)}
							<li class:unref={!referred}>
								<span class="fig-no mono">{t('struct.tableNo', { n: tcap.number ?? '?' })}</span>
								<span class="fig-cap">{excerpt(tcap.raw.replace(/^(tablo|table)\s*\d+[:.)\s-]*/i, ''), 90)}</span>
								<span class="chip {referred ? 'chip-ok' : 'chip-warning'}">
									{referred ? t('struct.referenced') : t('struct.unreferenced')}
								</span>
							</li>
						{/each}
					</ul>
				</div>
				<div class="fig-block">
					<h4>{t('struct.figures')} <span class="mono count">{s.figures.length}</span></h4>
					<ul class="fig-list">
						{#each s.figures as fig (fig.id)}
							{@const referred = s.figureRefs.some((r) => r.number === fig.number)}
							<li class:unref={!referred}>
								<span class="fig-no mono">{t('struct.figureNo', { n: fig.number ?? '?' })}</span>
								<span class="fig-cap">{excerpt(fig.raw.replace(/^(şekil|sekil|figure|fig\.)\s*\d+[:.)\s-]*/i, ''), 90)}</span>
								<span class="chip {referred ? 'chip-ok' : 'chip-warning'}">
									{referred ? t('struct.referenced') : t('struct.unreferenced')}
								</span>
							</li>
						{/each}
					</ul>
				</div>
			{/if}

			{#if s.danglingRefs.length}
				<p class="small warn-line">
					{t('struct.dangling')} {s.danglingRefs.map((r) => r.raw).join(', ')}
				</p>
			{/if}
		</section>
	</div>

	<section class="card panel wide">
		<h3>{t('struct.languageTitle')}</h3>
		<p class="small muted panel-sub">
			{t('struct.languageSub')}
			<strong>{designLabel(report.language.design)}</strong>
			{#if report.language.designEvidence}
				· {t('struct.designEvidence')} <span class="mono">{report.language.designEvidence}</span>
			{/if}
		</p>

		{#if report.language.hits.length === 0}
			<p class="empty muted">{t('struct.emptyLang')}</p>
		{:else}
			<ul class="lang-list">
				{#each report.language.hits.slice(0, 40) as h (h.id)}
					<li class="lang" class:justified={h.justified}>
						<span
							class="chip {h.pattern === 'causal'
								? h.justified
									? 'chip-ok'
									: 'chip-warning'
								: h.pattern === 'absolutist'
									? 'chip-critical'
									: 'chip-info'}"
						>
							{langChip(h)}
						</span>
						<q>{h.match}</q>
						<p class="sentence">{excerpt(h.sentence, 220)}</p>
					</li>
				{/each}
			</ul>
		{/if}
	</section>
</div>

<style>
	.struct-tab {
		display: grid;
		gap: 1rem;
	}
	.cols {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1rem;
	}
	@media (max-width: 900px) {
		.cols {
			grid-template-columns: 1fr;
		}
	}
	.panel {
		padding: 1.15rem 1.25rem;
	}
	.panel h3 {
		font-family: var(--font-ui);
		font-size: 0.92rem;
		font-weight: 650;
		margin: 0;
	}
	.panel h4 {
		font-family: var(--font-ui);
		font-size: 0.78rem;
		font-weight: 650;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--muted);
		margin: 0.9rem 0 0.4rem;
	}
	.panel-sub {
		margin: 0.15rem 0 0.9rem;
	}
	.sections {
		list-style: none;
		padding: 0;
		margin: 0;
		display: grid;
		gap: 0.15rem;
	}
	.sections li {
		display: flex;
		align-items: baseline;
		gap: 0.5rem;
		padding: 0.32rem 0;
		border-bottom: 1px solid var(--line);
		font-size: 0.84rem;
		flex-wrap: wrap;
	}
	.sections li:last-child {
		border-bottom: 0;
	}
	.mark {
		width: 14px;
		color: var(--ok);
		flex: none;
	}
	.missing .mark {
		color: var(--muted);
	}
	.sec-name {
		font-weight: 550;
		min-width: 100px;
	}
	.sec-heading {
		color: var(--muted);
		font-size: 0.78rem;
	}
	.sec-line {
		color: var(--muted);
		font-size: 0.7rem;
	}
	.sec-missing {
		color: var(--warning);
		font-size: 0.78rem;
	}
	.missing {
		color: var(--ink-2);
	}
	.fig-block {
		margin-bottom: 0.5rem;
	}
	.fig-list {
		list-style: none;
		padding: 0;
		margin: 0;
		display: grid;
		gap: 0.3rem;
	}
	.fig-list li {
		display: grid;
		grid-template-columns: 72px 1fr auto;
		gap: 0.5rem;
		align-items: baseline;
		font-size: 0.8rem;
		padding: 0.2rem 0;
	}
	.fig-cap {
		color: var(--muted);
		font-size: 0.77rem;
	}
	.count {
		color: var(--muted);
		font-weight: 400;
	}
	.warn-line {
		margin: 0.7rem 0 0;
		color: var(--warning);
		font-size: 0.78rem;
	}
	.empty {
		padding: 0.6rem 0;
	}
	.lang-list {
		list-style: none;
		padding: 0;
		margin: 0;
		display: grid;
		gap: 0.6rem;
	}
	.lang {
		display: grid;
		gap: 0.25rem;
		padding: 0.55rem 0.7rem;
		border-left: 2px solid var(--warning);
		background: var(--paper);
		border-radius: 0 7px 7px 0;
	}
	.lang.justified {
		border-left-color: var(--ok);
	}
	.lang q {
		font-style: italic;
		font-size: 0.86rem;
	}
	.sentence {
		margin: 0;
		font-size: 0.78rem;
		color: var(--muted);
	}
</style>
